import { useAxios } from '@/utils/axios/useAxios';
import { routeLoginInterceptor } from '@/modules/app-bridge/utils/RouteLoginInterceptor';
import { unauthorizedResponseInterceptor } from '@/modules/app-bridge/utils/UnauthorizedResponseInterceptor';
import type { AxiosRequestConfig } from 'axios';
import type { NuxtApp } from '#app';

export default defineNuxtPlugin({
  name: 'app-bridge',
  dependsOn: ['axios', 'init-base-store', 'app-bridge-init'],
  parallel: true,
  async setup(nuxtApp) {
    const $log = logger.createLogMethods('app-bridge:plugin');

    if (import.meta.server) {
      $log.debug(`Skipping initialization, is server-side.`);
      return {};
    }

    if (!useMainStore().isAppview) {
      $log.debug(`Skipping initialization, is not appview.`);
      return {};
    }

    $log.debug(`Initializing...`);

    const $appBridge = useAppBridge();
    const { $axios, $router } = nuxtApp as NuxtApp;
    const userStore = useUserStore();

    // Clone axios instance to use inside auto-retry response error interceptor without triggering itself recursively
    const axiosBase = useAxios({ ...$axios.defaults } as AxiosRequestConfig);

    // Auto-retry failed requests with fresh token when authentication expires (401 response status)
    useAxiosResponseErrorInterceptor(
      () => $appBridge.isActiveCDC,
      unauthorizedResponseInterceptor($log, axiosBase, userStore),
    );

    // Auto-authenticate via bridge when user navigates to login page
    // Note: Route interceptor behavior is inconsistent for SSR and CSR:
    //  - Initial page load: server-side triggers, client-side not
    //  - Page navigation: server-side not used, client-side triggers
    // This would mean we don't need an interceptor during SSR, but instead only this function call here. TODO: ??
    const interceptorInstance = routeLoginInterceptor($appBridge, userStore);

    // Manually invoke interceptor, this call is responsible for having two plugins
    await interceptorInstance(unref($router.currentRoute));

    // Register interceptor
    useRouteInterceptor(() => $appBridge.isActiveCDC, interceptorInstance);

    return {};
  },
});
